import { useCallback, useMemo } from "react";
import { SearchOption, TypeaheadInput, TypeaheadInputComponent } from "@jsq/ds/core";
import { withClientContext } from "~/apollo/client";
import { useGetEntitiesWithSearchLazyQuery } from "~/graphql/mainHooks";
import * as styles from "../styles_vanilla.css";

export type EntityOption = {
  id: string;
  label: string;
  program: string | undefined;
};

type EntityTypeaheadProps = {
  arenaId?: string;
  onSelect: (option: EntityOption) => void;
  onClear: () => void;
  inputRef: React.RefObject<TypeaheadInputComponent>;
  disabled?: boolean;
  placeholder?: string;
  initialSearchValue?: string;
};

export const EntityTypeahead = ({
  arenaId,
  onSelect,
  onClear,
  inputRef,
  disabled,
  placeholder,
  initialSearchValue,
}: EntityTypeaheadProps) => {
  const [getEntities, { loading, error, data }] = useGetEntitiesWithSearchLazyQuery({
    variables: { arenaId: undefined, searchTerm: "", filterEntitiesWithTransactions: false },
    fetchPolicy: "no-cache",
    context: withClientContext("staff"),
  });

  const nodes = useMemo(
    () =>
      data?.investmentEntitiesByArena?.edges?.flatMap((e) => (e?.node ? [e.node] : [])) ?? [],
    [data],
  );

  const options: SearchOption[] = useMemo(
    () => nodes.map((node) => ({ id: node.id, label: node.name, type: "account" })),
    [nodes],
  );

  const handleSearch = useCallback(
    (term: string) =>
      void getEntities({
        variables: { searchTerm: term.trim(), arenaId, filterEntitiesWithTransactions: false },
      }),
    [arenaId, getEntities],
  );

  const handleSelect = useCallback(
    (selected: SearchOption) => {
      const program = nodes.find((node) => node.id === selected.id)?.complianceSettings
        ?.complianceProgram;
      onSelect({
        id: selected.id.toString(),
        label: selected.label,
        program: program?.toString(),
      });
    },
    [nodes, onSelect],
  );

  return (
    <TypeaheadInput
      label="Entity"
      name="entityId"
      disabled={disabled || !arenaId}
      placeholder={placeholder ?? ""}
      className={styles.typeaheadField}
      options={options}
      isLoading={loading}
      isError={error !== undefined}
      onDebouncedSearch={handleSearch}
      onSelectOption={handleSelect}
      onClearValue={onClear}
      initialSearchValue={initialSearchValue}
      ref={inputRef}
    />
  );
};
